/* eslint-disable react/no-children-prop */
import PropTypes from "prop-types";
import React from "react";
import first from "lodash/first";
import { FormattedMessage } from "react-intl";
import ProgressiveImg from "../.shared/ProgressiveImg.jsx";
import BackgroundProgressiveImg from "../.shared/BackgroundProgressiveImg.jsx";
import NegosList from "../.shared/NegosList.jsx";
import DurationLabel from "app/pages/.shared/DurationLabel";
import PassengersCounterDisplay from "../.shared/PassengersCounterDisplay.jsx";
import Timer from "../.shared/Timer.jsx";
import Period from "../.shared/Period.jsx";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { OFFER_CONTRACT_TYPES, OFFER_TYPES } from "app/constants";
import { bookingPropTypes, ficheProduitPropTypes } from "app/utils/propTypes";
import ProductDestinationTitleContainer from "app/pages/Listing/components/ProductDestinationTitleContainer";
import MyFlightDetail from "./MyFlightDetail.jsx";
import AmountContainer from "app/pages/.shared/AmountContainer";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";

import "./MyBooking.scss";
import OccupanciesDisplayLabel from "app/pages/.shared/OccupanciesDisplayLabel/OccupanciesDisplayLabel";
import Collapse from "rc-collapse";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";

const IMG_SIZES_MOBILE = {
	small: { width: 768 },
	medium: { width: 978 },
};

const IMG_CLOUDINARY_OPTIONS = ["c_fill", "g_center"];

const IMG_SIZES_DESKTOP = {
	large: { height: 185, width: 341 },
	xlarge: { height: 185, width: 341 },
};

const MyBooking = ({
	ficheProduit,
	photo,
	negos,
	booking,
	resolution,
	inboundArrivalDayCount,
	outboundArrivalDayCount,
	quoteTotal,
	showTimer,
	showNegotiated,
	showFlightDetail,
	showPriceDetails,
	isSDP,
}) => {
	let photoNode = false;

	if (photo) {
		photoNode =
			resolution === RESOLUTION.UNKNOWN ||
			resolution === RESOLUTION.SMALL ||
			resolution === RESOLUTION.MEDIUM ? (
				<BackgroundProgressiveImg
					sizes={IMG_SIZES_MOBILE}
					thumbnail={photo.thumbnail}
					src={photo.url}
					alt={photo.alt}
					cloudinaryOptions={IMG_CLOUDINARY_OPTIONS}
				/>
			) : (
				<ProgressiveImg
					sizes={IMG_SIZES_DESKTOP}
					src={photo.url}
					alt={photo.alt}
					thumbnail={photo.thumbnail}
				/>
			);
	}

	const startDateLabel =
		booking.offer && booking.offer.type === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION ? (
			<FormattedMessage id="my.booking.flight.start.date.label" />
		) : (
			<FormattedMessage id="my.booking.accommodation.start.date.label" />
		);

	const endDateLabel =
		booking.offer && booking.offer.type === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION ? (
			<FormattedMessage id="my.booking.flight.end.date.label" />
		) : (
			<FormattedMessage id="my.booking.accommodation.end.date.label" />
		);

	let inboundDepartureDate = booking.endDate;

	if (booking.offer && booking.offer.type === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION) {
		inboundDepartureDate =
			booking.flight &&
			booking.flight.inbound &&
			booking.flight.inbound.legs &&
			booking.flight.inbound.legs.length > 0
				? first(booking.flight.inbound.legs).from.takeOffDate
				: undefined;
	}

	const showSearchRecap =
		(booking.departureCity && booking.departureCity.label) ||
		(booking.duration && booking.duration.value > 0);

	const bookingSearchNode = showSearchRecap && (
		<div className="my-booking__search">
			{booking.departureCity && booking.departureCity.label && (
				<div className="my-booking__search-item">
					<label className="my-booking__label">
						<FormattedMessage id="my.booking.departure.city.label" /> :
					</label>
					<div className="my-booking__value">{booking.departureCity.label}</div>
				</div>
			)}

			{booking.duration && booking.duration.value && (
				<div className="my-booking__search-item">
					<label className="my-booking__label">
						<FormattedMessage id="my.booking.duration.label" />
					</label>
					<div className="my-booking__value">
						<DurationLabel duration={booking.duration.value} />
					</div>
				</div>
			)}

			{booking.departureDate && (
				<div className="my-booking__search-item">
					<label className="my-booking__label">{startDateLabel}</label>
					<div className="my-booking__value">
						<Period
							fromDate={booking.departureDate}
							format={{
								weekday: "short",
								day: "2-digit",
								month: "short",
							}}
						/>

						{outboundArrivalDayCount > 0 && (
							<div className="my-booking__arrival-days">
								<FormattedMessage
									id="my.booking.flight.arrival.day.count.label"
									values={{ days: outboundArrivalDayCount }}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			{inboundDepartureDate && (
				<div className="my-booking__search-item">
					<label className="my-booking__label">{endDateLabel}</label>
					<div className="my-booking__value">
						<Period
							fromDate={inboundDepartureDate}
							format={{
								weekday: "short",
								day: "2-digit",
								month: "short",
							}}
						/>

						{inboundArrivalDayCount > 0 && (
							<div className="my-booking__arrival-days">
								<FormattedMessage
									id="my.booking.flight.arrival.day.count.label"
									values={{ days: inboundArrivalDayCount }}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			{booking.departureDate &&
				booking.offer &&
				booking.offer.contractType !== OFFER_CONTRACT_TYPES.RENTAL && (
					<div className="my-booking__search-item">
						<label className="my-booking__label">
							<FormattedMessage id="my.booking.passengers.label" /> :
						</label>
						<div className="my-booking__value">
							{isSDP ? (
								<OccupanciesDisplayLabel occupancies={booking.occupancies} />
							) : (
								<PassengersCounterDisplay
									adults={booking.adults}
									children={booking.children}
									infants={booking.infants}
								/>
							)}
						</div>
					</div>
				)}
		</div>
	);

	return (
		<div className="my-booking">
			<div className="my-booking__picture">{photoNode}</div>

			<div className="my-booking__content">
				{showTimer && (
					<div className="my-booking__time-left">
						<Timer
							day={ficheProduit.expiration ? ficheProduit.expiration.day : undefined}
							hour={
								ficheProduit.expiration ? ficheProduit.expiration.hour : undefined
							}
							suffix={<FormattedMessage id="quotation.timer.left.label" />}
						/>
					</div>
				)}

				<div className="my-booking__product">
					<ProductDestinationTitleContainer
						region={ficheProduit.region}
						resort={ficheProduit.resort}
						country={ficheProduit.country}
					/>
					<ProductNameWithCategory
						categoryType={ficheProduit.categoryType}
						category={ficheProduit.category}
						name={ficheProduit.name}
					/>

					{bookingSearchNode}
				</div>
			</div>

			{bookingSearchNode}

			{showNegotiated && negos.length > 0 && (
				<Collapse
					className="my-booking__collapse my-booking__collapse--negotiated"
					expandIcon={({ isActive }) => {
						return (
							<IconArrowDown
								style={{
									transform: `rotate(${isActive ? 180 : 0}deg)`,
								}}
							/>
						);
					}}
				>
					<Collapse.Panel
						headerClass="my-booking__collapse-header"
						header={
							<div className="my-booking__collapse-title">
								<FormattedMessage id="my.booking.price.details.title" />
							</div>
						}
					>
						<NegosList hotelNegos={negos} />
					</Collapse.Panel>
				</Collapse>
			)}

			{showFlightDetail && (
				<Collapse
					className="my-booking__collapse my-booking__collapse--flight"
					expandIcon={({ isActive }) => {
						return (
							<IconArrowDown
								style={{
									transform: `rotate(${isActive ? 180 : 0}deg)`,
								}}
							/>
						);
					}}
				>
					<Collapse.Panel
						headerClass="my-booking__collapse-header"
						header={
							<div className="my-booking__collapse-title">
								<FormattedMessage id="my.booking.flights.details.title" />
							</div>
						}
					>
						<MyFlightDetail flight={booking.flight} />
					</Collapse.Panel>
				</Collapse>
			)}

			{showPriceDetails && (
				<div className="my-booking__quote-total">
					<div className="my-booking__quote-total__label">
						<FormattedMessage id="payment.total.quote.label" />
					</div>
					<AmountContainer amount={quoteTotal} />
				</div>
			)}
		</div>
	);
};

MyBooking.propTypes = {
	ficheProduit: ficheProduitPropTypes,
	booking: bookingPropTypes,
	photo: PropTypes.object,
	resolution: PropTypes.string,
	inboundArrivalDayCount: PropTypes.number,
	outboundArrivalDayCount: PropTypes.number,
	negos: PropTypes.array,
	quoteTotal: PropTypes.number,
	showTimer: PropTypes.bool,
	showPriceDetails: PropTypes.bool,
	showFlightDetail: PropTypes.bool,
	showNegotiated: PropTypes.bool,
	isSDP: PropTypes.bool,
};

MyBooking.defaultProps = {
	photo: {},
	ficheProduit: {},
	negos: [],
	booking: {},
};

export default React.memo(MyBooking);
